import {
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { VendorTypeEnum } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import type {
  RelatedLinkView,
  SeoContentView,
  StorefrontCardView,
} from '@zola/svc-marketplace-ts-types';

import type { SeoMetadata } from '~/pages/couples/explore/components/SearchResults/types/seoTypes';
import type { VendorSearchRequest } from '~/pages/couples/explore/components/SearchResults/types/types';
import { pickCanonicalFacets } from '~/pages/couples/explore/util/canonicalUrl';
import type {
  LocationOrAllMarketsType,
  TaxonomyNodeType,
  TaxonomyNodeView,
  VendorCardView,
} from '~/types/responseTypes';
import type { ClientApiServiceType } from '~/util/apiService';
import getCanonicalUrl from '~/util/storefrontUrl';

import ServerApiService, { ServerApiServiceType } from '../server/util/apiService';

export const getExpertAdviceRelatedLinks = async (
  topicSlug: string,
  subtopicSlug: string,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  return apiService.get<RelatedLinkView[]>(
    `/web-marketplace-api/v1/seo/related-links/topic/${topicSlug}/subtopic/${subtopicSlug}`
  );
};

export const getStorefrontRelatedLinks = async (
  uuid: string,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  return apiService
    .get<VendorCardView[]>(`/web-marketplace-api/v1/storefront/${uuid}/suggested-seo?limit=12`)
    .then((vendorCards): RelatedLinkView[] => {
      return vendorCards.map((vendorCard) => {
        const { taxonomyNode, storefrontSlug, vendorName } = vendorCard;
        const type = (taxonomyNode as TaxonomyNodeView).key;
        const slug = storefrontSlug as string;
        const urlPath = getCanonicalUrl(type, slug);
        const title = vendorName as string;
        return {
          title,
          urlPath,
        };
      });
    });
};

export const getStorefrontSimilarLinks = async ({
  apiService,
  limit = 12,
  storefrontUuid,
  vendorMarketId,
  vendorType,
}: {
  apiService: ServerApiServiceType | ClientApiServiceType;
  limit?: number;
  storefrontUuid: string;
  vendorMarketId: number;
  vendorType: VendorTypeEnum;
}) => {
  const body = { vendorTypes: [vendorType], vendorMarketIds: [vendorMarketId] };
  return apiService
    .post<StorefrontCardView[]>(
      `/web-marketplace-api/v1/storefront-suggestions/storefront/${storefrontUuid}/similar-storefronts?limit=${limit}`,
      body
    )
    .then((storefrontCards) =>
      storefrontCards.map((storefrontCard) => ({
        title: storefrontCard.name,
        urlPath: storefrontCard.slug,
      }))
    );
};

export const getRelatedLinksForVendorSearch = (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<RelatedLinkView[]> => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;

  const citySlug = selectedLocation?.type === 'CITY' ? selectedLocation?.slug : undefined;
  const taxonomyKey = selectedTaxonomy?.key;

  return getLocationAndTaxonomyRelatedLinks(citySlug, taxonomyKey, apiService);
};

export const getLocationAndTaxonomyRelatedLinks = async (
  citySlug: string | undefined,
  taxonomyKey: VendorTaxonomyKey | undefined,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  if (citySlug && taxonomyKey) {
    return apiService
      .get<RelatedLinkView[]>(
        `/web-marketplace-api/v1/seo/related-links/city/${citySlug}/taxonomy_node/${taxonomyKey}`
      )
      .catch(() => []);
  }
  return Promise.resolve([]);
};

/**
 * Get SEO metadata for the search results page
 *
 * NOTE: The search location can be a state, market or city, but this will only get metadata for cities
 */
export const getSeoMetaDataForSearchResultsPage = async (
  defaultKey: string,
  selectedLocation?: LocationOrAllMarketsType | null,
  selectedTaxonomy?: TaxonomyNodeType | null,
  supplied?: boolean | null,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<SeoMetadata> => {
  const { type, slug, marketSlug } = selectedLocation || {};
  const { key: taxonomyKey } = selectedTaxonomy || {};
  let url: string | undefined;

  if (
    selectedTaxonomy?.key &&
    [
      BANDS_DJS_TAXONOMY_KEY,
      CAKES_DESSERTS_TAXONOMY_KEY,
      CATERING_TAXONOMY_KEY,
      FLORISTS_TAXONOMY_KEY,
      HAIR_MAKEUP_TAXONOMY_KEY,
      PHOTOGRAPHERS_TAXONOMY_KEY,
      PLANNERS_TAXONOMY_KEY,
      VENUES_TAXONOMY_KEY,
      VIDEOGRAPHERS_TAXONOMY_KEY,
      BAR_SERVICES_TAXONOMY_KEY,
    ]
      .filter(Boolean)
      .includes(selectedTaxonomy?.key) &&
    type === 'CITY' &&
    slug &&
    marketSlug &&
    supplied
  ) {
    const queryParams = [
      `key=${selectedTaxonomy?.key}-template`,
      `vendorMarketKey=${marketSlug}`,
      `taxonomyKey=${taxonomyKey}`,
      `citySlug=${slug}`,
    ];
    url = `/web-marketplace-api/v1/metadata/seo-template?${queryParams.join('&')}`;
  } else {
    url = `/web-marketplace-api/v1/metadata/seo?key=${defaultKey}`;
  }

  if (url) {
    return apiService.get(url).catch(() => ({}));
  }

  return Promise.resolve({});
};

type SeoContentRequest = {
  vendorMarketKey: string;
  taxonomyNodeKey: string;
  appliedPrimaryFacet: string | null;
} & (
  | {
      citySlug: string;
      stateSlug: null;
    }
  | {
      citySlug: null;
      stateSlug: string;
    }
);

/**
 * Get SEO content block HTML for the search results page
 *
 * NOTE: The search location can be a state, market or city, but this will only get content for cities
 */
export const getSeoContentBlockHtml = async (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<SeoContentView | null> => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;
  const { type, slug, marketSlug } = selectedLocation || {};
  const taxonomyNodeKey = selectedTaxonomy?.key;

  const primaryFacet = pickCanonicalFacets(
    taxonomyNodeKey,
    vendorSearch.selectedFacets || [],
    true
  );
  const appliedPrimaryFacet = primaryFacet.length === 1 ? primaryFacet[0].key : null;

  if (type === 'CITY' && slug && marketSlug && taxonomyNodeKey) {
    const request: SeoContentRequest = {
      citySlug: slug,
      stateSlug: null,
      vendorMarketKey: marketSlug,
      taxonomyNodeKey,
      appliedPrimaryFacet,
    };

    return apiService
      .post<SeoContentView, SeoContentRequest>('/web-marketplace-api/v1/seo/content', request)
      .catch(() => null);
  }

  return Promise.resolve(null);
};
